/**
 * FleetManager V3 - Sidebar Component
 * Adaptive navigation based on user profile
 *
 * ============================================================================
 * HOW TO MANAGE SIDEBAR ITEMS
 * ============================================================================
 *
 * Navigation items are stored in the DATABASE, not in code.
 *
 * TABLE: app_views (port 9080)
 * ────────────────────────────────────────────────────────────────────────────
 * | Column       | Description                                               |
 * |--------------|-----------------------------------------------------------|
 * | view_code    | Unique ID (e.g., 'dashboard', 'vehicles', 'claims')       |
 * | view_name    | Display label in sidebar                                  |
 * | icon         | Lucide icon name (e.g., 'Car', 'Users', 'Shield')         |
 * | nav_group    | Group code (main, operations, risk, finance, advanced...) |
 * | nav_order    | Sort order within group                                   |
 * | is_enabled   | 1 = visible, 0 = hidden from sidebar                      |
 * | admin_only   | 1 = only admins see it                                    |
 * | show_count   | 1 = show count badge                                      |
 * ────────────────────────────────────────────────────────────────────────────
 *
 * TO HIDE A FEATURE:
 *   curl -X PUT "http://localhost:9080/api/v1/app_views/ID" \
 *        -H "Content-Type: application/json" \
 *        -d '{"is_enabled": 0}'
 *
 * TO SHOW A FEATURE:
 *   curl -X PUT "http://localhost:9080/api/v1/app_views/ID" \
 *        -H "Content-Type: application/json" \
 *        -d '{"is_enabled": 1}'
 *
 * TO LIST ALL FEATURES:
 *   curl "http://localhost:9080/api/v1/app_views"
 *
 * TO FIND A FEATURE BY CODE:
 *   curl "http://localhost:9080/api/v1/app_views?view_code=cockpit"
 *
 * TABLE: nav_groups (port 9080)
 * ────────────────────────────────────────────────────────────────────────────
 * | group_code     | group_name               | group_order |
 * |----------------|--------------------------|-------------|
 * | fleet          | Mon Parc                 | 1           |
 * | operations     | Operations               | 2           |
 * | insurance      | Administratif            | 3           |
 * | finance        | Finance & Rapports       | 4           |
 * | administration | Configuration            | 5           |
 * | platform       | Plateforme               | 6           |
 * ────────────────────────────────────────────────────────────────────────────
 *
 * API ENDPOINT: GET /api/v1/monitor/app_views
 *   Returns grouped navigation (only is_enabled=1 items)
 *
 * FALLBACK: If API fails, uses static config from:
 *   frontend/src/v3/config/navigation.js
 *
 * ============================================================================
 * LLM NOTE (Claude, GPT, etc.)
 * ============================================================================
 * When asked to add/remove/hide sidebar items:
 *   1. DO NOT edit this file or navigation.js for feature visibility
 *   2. Use the database via curl commands shown above
 *   3. The API automatically filters is_enabled=0 items
 *   4. Changes take effect on next page refresh (no rebuild needed)
 *
 * When asked to ADD a new feature to sidebar:
 *   1. Create the component in frontend/src/components/
 *   2. Add case in frontend/src/v3/App.jsx renderView()
 *   3. INSERT into app_views table with is_enabled=1
 *   4. Ensure icon exists in ICONS map below
 *
 * Current disabled features (is_enabled=0):
 *   - cockpit (id=89)
 *   - risk-profiles (id=96)
 *   - analytics (id=106)
 *
 * LABELS & LANGUAGE (i18n, 2026-08-21)
 *   view_name / group_name in the DB are the FRENCH reference. At render time the
 *   sidebar looks up `nav.<view_code>` (items) and `nav.groups.<group_code>` (groups)
 *   in frontend/src/i18n/locales/<lang>.json and falls back to the DB label when
 *   the key is missing. Adding a view = INSERT in app_views + one line per locale.
 *   No label_en column in DB: languages are a frontend deliverable (one file = one language).
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Car, Users, Wrench, Fuel, FileText, AlertTriangle, Shield, CreditCard,
  MapPin, BarChart3, TrendingUp, LayoutDashboard, ClipboardCheck, ShieldCheck,
  Hammer, ChevronDown, ChevronRight, Building2, UserCog, Settings, Server, Scale,
  Activity, Archive, UserCheck, Gauge, Share2, Circle, ListChecks, Send, Link, Mail,
  LayoutList, Calendar, Receipt
} from 'lucide-react';
import { useProfile } from '../../hooks/useProfile.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { usePipelineOptional } from '../../context/PipelineContext.jsx';
import { useVisualHelp } from '../help/VisualHelpProvider.jsx';
import { useTour } from '../tour/useTour.js';
import { useTranslation } from 'react-i18next';
import { getFilteredNavigation, getFlatNavigation, NAVIGATION } from '../../config/navigation.js';
import { monitorAPI, sidebarAPI } from '../../../api.js';
import { APP_VERSION } from '../../../version';

// Icon mapping - includes all possible icons from both static and dynamic sources
const ICONS = {
  LayoutDashboard, Car, Users, Wrench, Fuel, FileText, AlertTriangle,
  Shield, CreditCard, MapPin, BarChart3, TrendingUp, ClipboardCheck,
  ShieldCheck, Hammer, Building2, UserCog, Settings, Server, Scale,
  Activity, Archive, UserCheck, Gauge, Share2, Circle, ListChecks,
  Send, Link, Mail, LayoutList, Calendar, Receipt
};

/**
 * E-Invoices feature flag (contrat einvoice §7) — tenant-scoped, défaut false.
 *
 * En S2 il n'y a pas encore d'endpoint tenant_features côté backend ; le flag
 * vit donc dans localStorage côté dev/tests. Quand l'endpoint atterrira (S5+),
 * remplacer ce helper par la lecture du hook tenant standard. Voir contrat §7.
 *
 *   // activer en dev :
 *   localStorage.setItem('fm_feature_e_invoices_enabled', 'true'); location.reload();
 */
function isEInvoicesEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('fm_feature_e_invoices_enabled') === 'true';
  } catch {
    return false;
  }
}

// Profile icons
const PROFILE_ICONS = {
  Building2, UserCog, BarChart3
};

/**
 * Transform API app_views response to navigation format expected by components
 */
function transformApiToNavigation(apiData) {
  if (!apiData?.groups) return null;

  const result = {};

  Object.entries(apiData.groups).forEach(([groupCode, group]) => {
    if (group.items && group.items.length > 0) {
      result[groupCode] = {
        label: group.group_name,
        order: group.group_order || 100,
        adminOnly: group.admin_only === 1,
        superuserOnly: group.superuser_only === 1,
        defaultExpanded: group.default_expanded === 1,
        isCollapsible: group.is_collapsible !== 0, // default true
        items: group.items.map(item => ({
          id: item.view_code,
          icon: item.icon || 'Circle',
          label: item.view_name,
          showCount: item.show_count === 1,
          adminOnly: item.admin_only === 1,
          superuserOnly: item.superuser_only === 1,
        }))
      };
    }
  });

  return result;
}

/**
 * Transform Sidebar Orchestrator response to navigation format
 * Orchestrator returns pre-filtered, pre-sorted groups with visibility applied
 */
function transformOrchestratorToNavigation(resolverData) {
  if (!resolverData?.groups) return null;

  const result = {};

  Object.entries(resolverData.groups).forEach(([groupCode, group]) => {
    if (group.items && group.items.length > 0) {
      result[groupCode] = {
        label: group.label,
        labelCustom: group.labelCustom === true,
        order: group.order || 100,
        adminOnly: group.adminOnly || false,
        superuserOnly: group.superuserOnly || false,
        defaultExpanded: group.defaultExpanded || false,
        isCollapsible: group.isCollapsible !== false,
        items: group.items.map(item => ({
          id: item.id,
          icon: item.icon || 'Circle',
          label: item.label,
          showCount: item.showCount || false,
          adminOnly: item.adminOnly || false,
          superuserOnly: item.superuserOnly || false,
        }))
      };
    }
  });

  return result;
}

/**
 * Filter dynamic navigation based on profile modules and user permissions
 */
function filterDynamicNavigation(navigation, profileModules, options = {}) {
  const { isAdmin = false, isSuperuser = false } = options;
  const result = {};

  Object.entries(navigation).forEach(([groupKey, group]) => {
    // Skip admin-only groups for non-admins
    if (group.adminOnly && !isAdmin) {
      return;
    }

    // Skip superuser-only groups for non-superusers
    if (group.superuserOnly && !isSuperuser) {
      return;
    }

    // For admin/superuser groups, show all items (they're not module-controlled)
    let visibleItems;
    if (group.adminOnly || group.superuserOnly) {
      visibleItems = group.items.filter(item => {
        if (item.superuserOnly && !isSuperuser) {
          return false;
        }
        return true;
      });
    } else {
      visibleItems = group.items.filter(item => profileModules.includes(item.id));
    }

    if (visibleItems.length > 0) {
      result[groupKey] = {
        ...group,
        items: visibleItems
      };
    }
  });

  return result;
}

/**
 * Get flat navigation from dynamic source
 */
function getFlatDynamicNavigation(navigation, profileModules) {
  const allItems = [];

  Object.values(navigation)
    .sort((a, b) => (a.order || 100) - (b.order || 100))
    .forEach(group => {
      if (group.items) {
        group.items.forEach(item => {
          if (profileModules.includes(item.id)) {
            allItems.push(item);
          }
        });
      }
    });

  return allItems;
}

/** Translated label of a view: `nav.<view_code>`, DB/static label as fallback. */
function useNavLabel() {
  const { t } = useTranslation();
  return {
    item: (item) => t(`nav.${item.id}`, { defaultValue: item.label || item.id }),
    // Un libellé POSÉ PAR LE PARAMÉTRAGE (surcharge de profil/tenant) se rend tel quel :
    // le retraduire par `nav.groups.<code>` rendrait le libellé du catalogue à sa place.
    group: (groupKey, group) => (
      group.labelCustom ? group.label : (group.label ? t(`nav.groups.${groupKey}`, { defaultValue: group.label }) : '')
    ),
  };
}

function NavItem({ item, isActive, onClick, counts = {}, isSpotlight = false }) {
  const label = useNavLabel();
  const Icon = ICONS[item.icon] || Circle;
  const count = item.showCount ? counts[item.id] : null;

  return (
    <button
      onClick={() => onClick(item.id)}
      data-nav-item={item.id}
      data-sidebar-item={item.id}
      data-tour={`nav-${item.id}`}
      className={`
        w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm mb-0.5
        transition-all relative
        ${isActive
          ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400'
          : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-slate-800/50'
        }
        ${isSpotlight
          ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 bg-amber-50 dark:bg-amber-500/20 animate-pulse'
          : ''
        }
      `}
    >
      <Icon size={16} className={isSpotlight ? 'text-amber-500' : ''} />
      <span className="flex-1 text-left">{label.item(item)}</span>
      {count !== null && count !== undefined && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300">
          {count}
        </span>
      )}
      {/* Spotlight arrow indicator */}
      {isSpotlight && (
        <span className="absolute -right-1 top-1/2 -translate-y-1/2 text-amber-500 text-lg font-bold animate-bounce">
          ←
        </span>
      )}
    </button>
  );
}

function NavGroup({ groupKey, group, activeView, onNavigate, counts, isExpanded, onToggle, spotlightTarget }) {
  const label = useNavLabel();
  return (
    <div className="mb-2">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 dark:text-slate-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-slate-400"
      >
        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {label.group(groupKey, group)}
      </button>
      {isExpanded && (
        <div className="mt-0.5">
          {group.items.map(item => (
            <NavItem
              key={item.id}
              item={item}
              isActive={activeView === item.id}
              onClick={onNavigate}
              counts={counts}
              isSpotlight={spotlightTarget === `sidebar-${item.id}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ activeView, onNavigate, counts = {} }) {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const { isSuperuser, user, hasFeature } = useAuth();
  const { spotlightTarget } = useVisualHelp();
  const { isActive: isTourActive } = useTour();
  const ProfileIcon = PROFILE_ICONS[profile.icon] || Building2;

  // State for dynamic navigation
  const [dynamicNavigation, setDynamicNavigation] = useState(null);
  const [navSource, setNavSource] = useState('static'); // 'static', 'dynamic', or 'orchestrator'
  const [isLoading, setIsLoading] = useState(true);
  const [orchestratorHash, setOrchestratorHash] = useState(null);

  // Track expanded groups (for grouped/full nav styles)
  // Start with minimal defaults, will be populated from API response
  const [expandedGroups, setExpandedGroups] = useState({});

  // Fetch dynamic navigation on mount
  // Supports both legacy app_views API and new Sidebar Orchestrator (Phase 1)
  useEffect(() => {
    let isMounted = true;

    async function fetchNavigation() {
      try {
        // Sidebar Orchestrator is now the default navigation source.
        // Can be disabled via: localStorage.setItem('sidebar_orchestrator_enabled', 'false')
        const orchestratorDisabled = typeof window !== 'undefined' &&
          localStorage.getItem('sidebar_orchestrator_enabled') === 'false';

        if (!orchestratorDisabled) {
          // Use new Sidebar Orchestrator API
          const response = await sidebarAPI.resolve({
            profileCode: profile.code,
            mode: 'elusive',
          });
          if (isMounted && response?.ok && response?.data) {
            const transformed = transformOrchestratorToNavigation(response.data);
            if (transformed && Object.keys(transformed).length > 0) {
              setDynamicNavigation(transformed);
              setNavSource('orchestrator');
              setOrchestratorHash(response.data.meta?.hash || null);
              if (process.env.NODE_ENV === 'development') {
                console.debug('[Sidebar] Orchestrator loaded', {
                  hash: response.data.meta?.hash,
                  groups: Object.keys(transformed).length,
                });
              }
            }
          }
        } else {
          // Legacy: Use app_views API
          const response = await monitorAPI.getAppViews();
          if (isMounted && response?.ok && response?.data) {
            const transformed = transformApiToNavigation(response.data);
            if (transformed && Object.keys(transformed).length > 0) {
              setDynamicNavigation(transformed);
              setNavSource('dynamic');
            }
          }
        }
      } catch (err) {
        console.warn('Failed to fetch navigation, using static config:', err.message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchNavigation();
    return () => { isMounted = false; };
  }, [profile.code]);

  // Generate expanded groups dynamically from navigation source
  useEffect(() => {
    const generateExpanded = (navigation) => {
      const expanded = {};
      Object.entries(navigation).forEach(([groupKey, group]) => {
        if (profile.navStyle === 'full') {
          // In full mode, all groups expanded
          expanded[groupKey] = true;
        } else {
          // Use defaultExpanded from source, fallback to false
          expanded[groupKey] = group.defaultExpanded ?? false;
        }
      });
      // All groups start collapsed — user expands what they need
      return expanded;
    };

    if ((navSource === 'orchestrator' || navSource === 'dynamic') && dynamicNavigation) {
      setExpandedGroups(generateExpanded(dynamicNavigation));
    } else if (navSource === 'static' && !isLoading) {
      // Use static navigation defaults
      setExpandedGroups(generateExpanded(NAVIGATION.groups));
    }
  }, [navSource, dynamicNavigation, profile.navStyle, isLoading]);

  const toggleGroup = useCallback((groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  }, []);

  // Check if user is admin based on their role
  const isAdmin = user?.role === 'admin' || isSuperuser;
  const isMinimal = profile.navStyle === 'minimal';

  // Check if pipeline mode is enabled
  const pipeline = usePipelineOptional();
  const pipelineEnabled = pipeline?.pipelineEnabled ?? false;

  // E-Invoices : le flag tenant vient de /auth/me (ADR-011, feature.e_invoices) ; le
  // localStorage reste un forçage de dev. Masquage seulement : l'API répond 403 sans le flag.
  const eInvoicesEnabled = (hasFeature ? hasFeature('e_invoices') : false) || isEInvoicesEnabled();

  // Get navigation based on source (orchestrator, dynamic, or static)
  const filteredNav = React.useMemo(() => {
    let nav;

    if (navSource === 'orchestrator' && dynamicNavigation) {
      // Orchestrator: Already pre-filtered by resolver — do NOT re-filter by profile.modules
      if (isMinimal) {
        // Flatten groups into a single list without profile filtering (resolver already did it)
        const allItems = [];
        Object.values(dynamicNavigation)
          .sort((a, b) => (a.order || 100) - (b.order || 100))
          .forEach(group => {
            if (group.items) allItems.push(...group.items);
          });
        // Wrap in a single group for consistent rendering
        nav = { _flat: { label: '', items: allItems, order: 0 } };
      } else {
        nav = dynamicNavigation;
      }
    } else if (navSource === 'dynamic' && dynamicNavigation) {
      // Legacy: Use dynamic navigation from database
      if (isMinimal) {
        nav = getFlatDynamicNavigation(dynamicNavigation, profile.modules);
      } else {
        nav = filterDynamicNavigation(dynamicNavigation, profile.modules, { isAdmin, isSuperuser });
      }
    } else {
      // Fall back to static navigation
      if (isMinimal) {
        nav = getFlatNavigation(profile.modules);
      } else {
        nav = getFilteredNavigation(profile.modules, profile.navStyle, { isAdmin, isSuperuser });
      }
    }

    // Pipeline/Dashboard visibility logic:
    // - When pipeline mode is ENABLED: hide dashboard, show pipeline
    // - When pipeline mode is DISABLED: show dashboard, hide pipeline
    if (nav) {
      const result = {};
      Object.entries(nav).forEach(([groupKey, group]) => {
        const filteredItems = group.items.filter(item => {
          // When pipeline enabled: hide dashboard
          if (pipelineEnabled && item.id === 'dashboard') return false;
          // When pipeline disabled: hide pipeline
          if (!pipelineEnabled && item.id === 'pipeline') return false;
          return true;
        });
        if (filteredItems.length > 0) {
          result[groupKey] = { ...group, items: filteredItems };
        }
      });

      // Inject "Factures électroniques" sous feature flag tenant (contrat §7).
      // Insertion en mode flat ou dans le groupe Administratif si présent ;
      // sinon dans le groupe finance, sinon en queue de file. Default OFF.
      if (eInvoicesEnabled) {
        const eInvoicesItem = { id: 'e_invoices', icon: 'Receipt', label: t('nav.e_invoices') };
        // Mode minimal/flat
        if (result._flat) {
          if (!result._flat.items.find(i => i.id === 'e_invoices')) {
            result._flat.items.push(eInvoicesItem);
          }
        } else {
          const targetKey = result.insurance ? 'insurance'
            : result.finance ? 'finance'
            : Object.keys(result)[0];
          if (targetKey && !result[targetKey].items.find(i => i.id === 'e_invoices')) {
            result[targetKey] = {
              ...result[targetKey],
              items: [...result[targetKey].items, eInvoicesItem],
            };
          }
        }
      }

      return result;
    }

    return nav;
  }, [navSource, dynamicNavigation, isMinimal, profile.modules, profile.navStyle, isAdmin, isSuperuser, pipelineEnabled, eInvoicesEnabled, t]);

  return (
    <aside className="w-52 bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-800 flex flex-col" data-tour="sidebar">
      {/* Logo */}
      <div className="p-3 border-b border-gray-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <img src="/favicon.svg" alt={t('common.app.shortName')} className="w-8 h-8 rounded-lg" />
          <div>
            <div className="font-bold text-gray-900 dark:text-white text-sm">{t('common.app.shortName')}</div>
            <div className="text-xs text-gray-500 dark:text-slate-500">{t('sidebar.version', { version: APP_VERSION })}</div>
          </div>
        </div>
      </div>

      {/* Profile indicator with nav source badge - Only for admins/superusers */}
      {/* Regular users see their profile in the header instead */}
      {(isAdmin || isSuperuser) && (
        <div className="px-3 py-2">
          <div
            className="p-2 rounded-lg bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 flex items-center gap-2"
            style={{ borderLeftColor: profile.color, borderLeftWidth: '3px' }}
          >
            <ProfileIcon size={14} style={{ color: profile.color }} />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-900 dark:text-white font-medium truncate">{profile.name}</div>
              <div className="text-xs text-gray-500 dark:text-slate-500 truncate">{profile.description}</div>
            </div>
            {/* Nav source indicator (subtle) - dev/admin only */}
            {navSource === 'orchestrator' && (
              <div
                className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse"
                title={t('sidebar.sourceOrchestrator', { hash: orchestratorHash || t('common.states.notAvailable') })}
              />
            )}
            {navSource === 'dynamic' && (
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" title={t('sidebar.sourceDynamic')} />
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-2">
        {isLoading ? (
          <div className="text-xs text-gray-500 dark:text-slate-500 text-center py-4">{t('sidebar.loading')}</div>
        ) : isMinimal ? (
          // Minimal: flat list, no groups
          <div>
            {filteredNav.map(item => (
              <NavItem
                key={item.id}
                item={item}
                isActive={activeView === item.id}
                onClick={onNavigate}
                counts={counts}
                isSpotlight={spotlightTarget === `sidebar-${item.id}`}
              />
            ))}
          </div>
        ) : (
          // Grouped or Full: show groups
          Object.entries(filteredNav)
            .sort(([, a], [, b]) => (a.order || 100) - (b.order || 100))
            .map(([groupKey, group]) => (
              <NavGroup
                key={groupKey}
                groupKey={groupKey}
                group={group}
                activeView={activeView}
                onNavigate={onNavigate}
                counts={counts}
                isExpanded={isTourActive ? true : (expandedGroups[groupKey] ?? false)}
                onToggle={() => toggleGroup(groupKey)}
                spotlightTarget={spotlightTarget}
              />
            ))
        )}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 dark:border-slate-800">
        <div className="text-xs text-gray-500 dark:text-slate-500 text-center">
          {t('common.app.footer')}
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
